import { Component, h, Host, Prop, Element, Method, Watch } from '@stencil/core';
import { checkSlottedContentForInvalidHTML, hideFocusRingWhenUsingMouse } from '../../../global/utils/utils';
export class ButtonComponent {
  constructor() {
    /**
     * The size of the button
     */
    this.size = 'lg';
    /**
     * The type of button to render.  Either `submit` or `button`.  Do not use this property if you set a value to @href
     */
    this.inputType = 'button';
    /**
     * The type of button to be displayed
     */
    this.type = 'primary';
    /**
     * Determines if the loading svg is to be displayed
     */
    this.loading = false;
    /**
     * provide screen reader users with additional context or to convey information not available in HTML alone
     */
    this.srContext = '';
    /**
     * sets aria attributes on the component
     */
    this.ariaAttribute = [];
    /**
     * provide if button is reverse mode or default
     */
    this.mode = 'default';
    /**
     * specify if the target link to be opened in new window
     */
    this.openNewWindow = false;
    /**
     * Position of an Icon. A position of center is for use with an icon and no text
     */
    this.iconPosition = 'right';
    /**
     * Size of the Icon
     */
    this.iconSize = 'xxs';
  }
  setAriaAttribute() {
    if (this.inputElement && this.ariaAttribute) {
      this.ariaAttribute.map((item) => {
        this.inputElement.setAttribute(item.attribute, item.value);
      });
    }
  }
  setSrContext(newValue) {
    if (newValue) {
      this.srContext = newValue;
    }
  }
  /**
   * Sets focus to the button
   */
  async setFocus() {
    this.inputElement.focus();
  }
  /**
   * Public method to set the state of the button (ie for use with the agl-ds-menu). The default set state is active.
   */
  async setButtonState(setState, state = 'active') {
    if (setState) {
      this.inputElement.classList.add('agl-ds-' + this.type + '--' + this.mode + '-' + state);
    }
    else {
      this.inputElement.classList.remove('agl-ds-' + this.type + '--' + this.mode + '-' + state);
    }
  }
  /**
   * Public method to rotate and icon if present.
   */
  async rotateButtonIcon() {
    if (this.hasIcon()) {
      if (this.iconElement.classList.contains('rotate-icon-apply')) {
        this.iconElement.classList.add('rotate-icon-remove');
        this.iconElement.classList.remove('rotate-icon-apply');
      }
      else {
        this.iconElement.classList.add('rotate-icon-apply');
        this.iconElement.classList.remove('rotate-icon-remove');
      }
    }
  }
  componentWillLoad() {
    checkSlottedContentForInvalidHTML(this.host, ['agl-ds-button']);
    this.setSrContext(this.srContext);
  }
  componentDidLoad() {
    hideFocusRingWhenUsingMouse(this.inputElement);
    //As the default value is ButtonInputType.button just check if they have manually set it to the submit type
    //and warn if they have also set the href
    if (this.inputType === 'submit' && !!this.href) {
      console.warn('Using `inputType = submit` with href will result in the `inputType` being ignored. It will render an anchor tag as a button.');
    }
    this.setAriaAttribute();
  }
  /**
   * Render Icon
   */
  renderIcon() {
    return (h("agl-ds-icon", { ref: (el) => (this.iconElement = el), class: { 'button-icon': true, ['button-icon--' + this.iconPosition]: true }, icon: this.icon, iconPath: this.iconPath, size: this.iconSize }));
  }
  /**
   * Get loading Indicator Type
   */
  getLoadingIndicator() {
    const loadIndicatorType = (this.mode === 'default' && this.type === 'primary') || (this.mode === 'reverse' && this.type !== 'primary') ? 'inverse' : 'default';
    return h("agl-ds-loading-indicator", { size: "sm", type: loadIndicatorType });
  }
  hasIcon() {
    return !!(this.iconPath || this.icon);
  }
  render() {
    return (h(Host, null,
      this.href && (h("a", { class: {
          button: true,
          ['agl-ds-' + this.size]: true,
          ['icon-left']: this.iconPosition === 'left',
          ['icon-center--' + this.size]: this.iconPosition === 'center',
          ['agl-ds-' + this.type + '--' + this.mode]: true,
          'remove-underline': this.hasIcon()
        }, href: this.href, rel: this.openNewWindow ? 'noopener noreferrer' : '', target: this.openNewWindow ? '_blank' : '_self', ref: (el) => (this.inputElement = el) },
        !this.loading && h("slot", null),
        !this.loading && this.hasIcon() && this.renderIcon(),
        this.loading && this.getLoadingIndicator(),
        this.srContext ? h("span", { class: "sr-only" },
          " ",
          this.srContext) : '',
        this.openNewWindow ? h("span", { class: "sr-only" }, ", opens in new window") : '')),
      !this.href && (h("button", { class: {
          button: true,
          ['agl-ds-' + this.size]: true,
          ['icon-left']: this.iconPosition === 'left',
          ['icon-center--' + this.size]: this.iconPosition === 'center',
          ['agl-ds-' + this.type + '--' + this.mode]: true,
          'remove-underline': this.hasIcon()
        }, type: this.inputType, ref: (el) => (this.inputElement = el) },
        !this.loading && h("slot", null),
        !this.loading && this.hasIcon() && this.renderIcon(),
        this.loading && this.getLoadingIndicator(),
        this.srContext ? h("span", { class: "sr-only" },
          " ",
          this.srContext) : ''))));
  }
  static get is() { return "agl-ds-button"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() { return {
    "$": ["./button.component.scss"]
  }; }
  static get styleUrls() { return {
    "$": ["button.component.css"]
  }; }
  static get properties() { return {
    "size": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "ButtonSize",
        "resolved": "\"lg\" | \"sm\"",
        "references": {
          "ButtonSize": {
            "location": "import",
            "path": "./button.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The size of the button"
      },
      "attribute": "size",
      "reflect": false,
      "defaultValue": "'lg'"
    },
    "inputType": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "ButtonInputType",
        "resolved": "\"button\" | \"submit\"",
        "references": {
          "ButtonInputType": {
            "location": "import",
            "path": "./button.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The type of button to render.  Either `submit` or `button`.  Do not use this property if you set a value to @href"
      },
      "attribute": "input-type",
      "reflect": false,
      "defaultValue": "'button'"
    },
    "type": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "ButtonType",
        "resolved": "\"primary\" | \"secondary\" | \"tertiary\"",
        "references": {
          "ButtonType": {
            "location": "import",
            "path": "./button.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The type of button to be displayed"
      },
      "attribute": "type",
      "reflect": false,
      "defaultValue": "'primary'"
    },
    "loading": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Determines if the loading svg is to be displayed"
      },
      "attribute": "loading",
      "reflect": false,
      "defaultValue": "false"
    },
    "srContext": {
      "type": "string",
      "mutable": true,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "provide screen reader users with additional context or to convey information not available in HTML alone"
      },
      "attribute": "sr-context",
      "reflect": false,
      "defaultValue": "''"
    },
    "ariaAttribute": {
      "type": "unknown",
      "mutable": false,
      "complexType": {
        "original": "AriaAttributes[]",
        "resolved": "AriaAttributes[]",
        "references": {
          "AriaAttributes": {
            "location": "import",
            "path": "../../../global/component.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "sets aria attributes on the component"
      },
      "defaultValue": "[]"
    },
    "mode": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "ButtonMode",
        "resolved": "\"default\" | \"reverse\"",
        "references": {
          "ButtonMode": {
            "location": "import",
            "path": "./button.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "provide if button is reverse mode or default"
      },
      "attribute": "mode",
      "reflect": false,
      "defaultValue": "'default'"
    },
    "href": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "provide href property for the anchor element, if the href element is not present the button will be rendered as a button\nelement else it will be rendered as an anchor element"
      },
      "attribute": "href",
      "reflect": false
    },
    "openNewWindow": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "specify if the target link to be opened in new window"
      },
      "attribute": "open-new-window",
      "reflect": false,
      "defaultValue": "false"
    },
    "icon": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The raw string of the icon (E.G Raw SVG or a base 64 encoded png perhaps)\nIf both iconPath and icon are set, icon will be preferred over iconPath"
      },
      "attribute": "icon",
      "reflect": false
    },
    "iconPath": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The path of an icon to be displayed along with text"
      },
      "attribute": "icon-path",
      "reflect": false
    },
    "iconPosition": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "IconPosition",
        "resolved": "\"center\" | \"left\" | \"right\"",
        "references": {
          "IconPosition": {
            "location": "import",
            "path": "./button.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Position of an Icon. A position of center is for use with an icon and no text"
      },
      "attribute": "icon-position",
      "reflect": false,
      "defaultValue": "'right'"
    },
    "iconSize": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "IconSize",
        "resolved": "\"lg\" | \"md\" | \"sm\" | \"xs\" | \"xxs\"",
        "references": {
          "IconSize": {
            "location": "import",
            "path": "../icon/icon.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Size of the Icon"
      },
      "attribute": "icon-size",
      "reflect": false,
      "defaultValue": "'xxs'"
    }
  }; }
  static get methods() { return {
    "setFocus": {
      "complexType": {
        "signature": "() => Promise<void>",
        "parameters": [],
        "references": {
          "Promise": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Sets focus to the button",
        "tags": []
      }
    },
    "setButtonState": {
      "complexType": {
        "signature": "(setState: boolean, state?: string) => Promise<void>",
        "parameters": [{
            "tags": [],
            "text": ""
          }, {
            "tags": [],
            "text": ""
          }],
        "references": {
          "Promise": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Public method to set the state of the button (ie for use with the agl-ds-menu). The default set state is active.",
        "tags": []
      }
    },
    "rotateButtonIcon": {
      "complexType": {
        "signature": "() => Promise<void>",
        "parameters": [],
        "references": {
          "Promise": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Public method to rotate and icon if present.",
        "tags": []
      }
    }
  }; }
  static get elementRef() { return "host"; }
  static get watchers() { return [{
      "propName": "ariaAttribute",
      "methodName": "setAriaAttribute"
    }, {
      "propName": "srContext",
      "methodName": "setSrContext"
    }]; }
}
